import type { FC } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { Button } from "@jti/core-components/components/Button";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";

import { useFlavourCarousel } from "../../FlavourCarouselContext";
import type { FlavourProperties } from "../FlavourRating/FlavourRating";
import { FlavourRating } from "../FlavourRating/FlavourRating";

import styles from "./FlavourCarouselCard.module.scss";

export const PRODUCT_GRADIENTS = {
  blue1: "var(--des-product-gradient-blue1)",
  blue2: "var(--des-product-gradient-blue2)",
  blue3: "var(--des-product-gradient-blue3)",
  blue4: "var(--des-product-gradient-blue4)",
  brown1: "var(--des-product-gradient-brown1)",
  brown2: "var(--des-product-gradient-brown2)",
  brown3: "var(--des-product-gradient-brown3)",
  green1: "var(--des-product-gradient-green1)",
  green2: "var(--des-product-gradient-green2)",
  green3: "var(--des-product-gradient-green3)",
  grey1: "var(--des-product-gradient-grey1)",
  grey2: "var(--des-product-gradient-grey2)",
  grey3: "var(--des-product-gradient-grey3)",
  orange1: "var(--des-product-gradient-orange1)",
  orange2: "var(--des-product-gradient-orange2)",
  pink1: "var(--des-product-gradient-pink1)",
  pink2: "var(--des-product-gradient-pink2)",
  purple1: "var(--des-product-gradient-purple1)",
  purple2: "var(--des-product-gradient-purple2)",
  purple3: "var(--des-product-gradient-purple3)",
  red1: "var(--des-product-gradient-red1)",
  red2: "var(--des-product-gradient-red2)",
  red3: "var(--des-product-gradient-red3)",
  red4: "var(--des-product-gradient-red4)",
  teal: "var(--des-product-gradient-teal)",
  yellow1: "var(--des-product-gradient-yellow1)",
  yellow2: "var(--des-product-gradient-yellow2)",
  yellow3: "var(--des-product-gradient-yellow3)",
  yellow4: "var(--des-product-gradient-yellow4)",
};

const { sanitize } = dompurify;

export type FlavourCarouselCardProps = {
  image: {
    src: string;
    alt?: string;
  };
  title: string;
  description: string;
  backgroundColor?: string;
  backgroundGradient?: keyof typeof PRODUCT_GRADIENTS;
  theme: "light" | "dark";
  cta?: {
    label?: string;
    url?: string;
    target?: "_blank" | "_self";
  };
  flavours?: FlavourProperties[];
};

export const FlavourCarouselCard: FC<FlavourCarouselCardProps> = ({
  backgroundColor,
  backgroundGradient,
  image,
  title,
  description,
  theme,
  flavours,
  cta,
}) => {
  const { isInversed } = useFlavourCarousel();
  return (
    <div
      className={classNames(styles.flavourCard, {
        [styles.inversed]: isInversed,
        ["light-theme"]: theme === "light",
        ["dark-theme"]: theme === "dark",
      })}
      data-testid="flavourCarouselCard"
      style={{
        background: (backgroundGradient ? PRODUCT_GRADIENTS[backgroundGradient] : undefined) || backgroundColor || "",
      }}
    >
      {image && (
        <div className={styles.imageWrapper}>
          <ImageBehaviour src={image.src} alt={image.alt} className={styles.image} />
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.title}>{title}</div>
        <div
          className={styles.description}
          dangerouslySetInnerHTML={{
            __html: sanitize(description),
          }}
        ></div>
        {(flavours?.length ?? 0) > 0 && (
          <div className={styles.ratings}>
            {flavours?.map(({ label, rating }, index) => (
              <FlavourRating key={`${label}-${rating}-${index}`} label={label} rating={rating} />
            ))}
          </div>
        )}

        {cta?.label && (
          <Button size="sm" href={cta.url} className={styles.cta} target={cta.target}>
            {cta.label}
          </Button>
        )}
      </div>
    </div>
  );
};
